import { useEffect, useState } from "react";
import api from "../api/axios";
import Layout from "../components/Layout";
import Card from "../components/UI/Card";
import Button from "../components/UI/Button";
import { PageLoader } from "../components/UI/Spinner";
import { useAuth } from "../context/AuthContext";

// Roles that each role can manage (roles strictly below them)
const MANAGEABLE_ROLES = {
  SUPER_ADMIN: ["ADMIN", "MANAGER", "CALLER", "VIEWER"],
  ADMIN:       ["MANAGER", "CALLER", "VIEWER"],
  MANAGER:     ["CALLER", "VIEWER"],
  CALLER:      ["VIEWER"],
  VIEWER:      [],
};

const ROLE_META = {
  ADMIN:   { color: "#2563eb", bg: "#dbeafe" },
  MANAGER: { color: "#0891b2", bg: "#cffafe" },
  CALLER:  { color: "#059669", bg: "#d1fae5" },
  VIEWER:  { color: "#64748b", bg: "#f1f5f9" },
};

const GROUPS = [
  {
    label: "Dashboard",
    icon: "🗂",
    keys: [["dashboard.view", "View Dashboard"]],
  },
  {
    label: "Records",
    icon: "📋",
    keys: [
      ["records.view",   "View Records"],
      ["records.upload", "Upload CSV"],
      ["records.edit",   "Edit Records"],
      // ["records.delete", "Delete Records"],
      ["records.assign", "TC Name Assignment"],
    ],
  },
  {
    label: "Users",
    icon: "👥",
    keys: [
      ["users.view",          "View Users"],
      ["users.create",        "Create Users"],
      ["users.update",        "Update Users"],
      ["users.disable",       "Enable / Disable"],
      ["users.resetPassword", "Reset Password"],
    ],
  },
  {
    label: "System",
    icon: "⚙️",
    keys: [
      ["access.manage", "Manage Access"],
      ["theme.update",  "Update Theme"],
    ],
  },
];

export default function AccessManagement() {
  const { user } = useAuth();
  const visibleRoles = MANAGEABLE_ROLES[user?.role] || [];

  if (visibleRoles.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full opacity-40">
          <div className="text-center">
            <div className="text-4xl mb-3">🔒</div>
            <div className="text-sm font-semibold">You don't have access to this page.</div>
          </div>
        </div>
      </Layout>
    );
  }

  const [items, setItems]           = useState([]);
  const [role, setRole]             = useState(visibleRoles[0] || "");
  const [permissions, setPermissions] = useState(null);
  const [msg, setMsg]               = useState("");
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const found = items.find(i => i.role === role);
    setPermissions(found?.permissions || null);
  }, [items, role]);

  function toggle(path) {
    const keys = path.split(".");
    setPermissions((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = obj[keys[i]] || {};
        obj = obj[keys[i]];
      }
      const last = keys[keys.length - 1];
      obj[last] = !obj[last];
      return next;
    });
  }

  function getVal(key) {
    return !!key.split(".").reduce((a, k) => (a ? a[k] : undefined), permissions);
  }

  async function save() {
    setMsg(""); setSaving(true);
    try {
      await api.put(`/permissions/${role}`, { permissions });
      setMsg("✅ Saved");
      await load();
    } finally {
      setSaving(false);
    }
  }

  const rm = ROLE_META[role] || ROLE_META.VIEWER;

  return (
    <Layout>
      <div className="max-w-3xl flex flex-col gap-5">

        {/* ── Header ── */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "rgb(var(--primary))", opacity: 0.7 }}>
            System
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Access Management</h1>
          <p className="text-sm opacity-40 mt-1">Control what each role can see and do.</p>
        </div>

        {/* ── Role tabs + Save button on top ── */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {visibleRoles.map(r => {
              const m = ROLE_META[r];
              return (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className="px-4 py-2 rounded-xl text-sm font-bold transition-all border-2"
                  style={role === r
                    ? { background: m.color, color: "white", borderColor: m.color }
                    : { background: "transparent", color: "rgb(var(--fg))", borderColor: "rgb(var(--border))", opacity: 0.55 }
                  }
                >
                  {r}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={save} loading={saving}>Save Permissions</Button>
            {msg && (
              <div className="text-sm px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                {msg}
              </div>
            )}
          </div>
        </div>

        {/* ── Permissions ── */}
        {loading ? (
          <Card className="overflow-hidden">
            <PageLoader message="Loading permissions..." />
          </Card>
        ) : (
          <>
            {/* Role info strip */}
            <div
              className="flex items-center gap-3 px-5 py-3 rounded-2xl border"
              style={{ background: rm.bg, borderColor: rm.color + "40" }}
            >
              <div className="w-3 h-3 rounded-full" style={{ background: rm.color }} />
              <span className="text-sm font-bold" style={{ color: rm.color }}>
                Editing permissions for: {role}
              </span>
            </div>

            {/* Groups */}
            {GROUPS.map(g => (
              <Card key={g.label} className="overflow-hidden">
                {/* Group header */}
                <div
                  className="flex items-center gap-2.5 px-5 py-3 border-b border-[rgb(var(--border))]"
                  style={{ background: "rgba(var(--primary),0.04)" }}
                >
                  <span className="text-base">{g.icon}</span>
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgb(var(--primary))" }}>
                    {g.label}
                  </span>
                </div>

                {/* Permission toggles */}
                <div className="p-3 grid md:grid-cols-2 gap-1">
                  {g.keys.map(([key, label]) => {
                    const on = getVal(key);
                    return (
                      <label
                        key={key}
                        className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl cursor-pointer transition-colors"
                        style={{ background: on ? "rgba(var(--primary),0.06)" : "transparent" }}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-2 h-2 rounded-full transition-colors"
                            style={{ background: on ? "rgb(var(--primary))" : "rgb(var(--border))" }}
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </div>
                        <input
                          type="checkbox"
                          className="toggle"
                          checked={on}
                          onChange={() => toggle(key)}
                        />
                      </label>
                    );
                  })}
                </div>
              </Card>
            ))}

            {/* Save bar */}
            {/* <div className="flex items-center gap-3 pt-1">
              <Button onClick={save} loading={saving}>Save Permissions</Button>
              {msg && (
                <div className="text-sm px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  {msg}
                </div>
              )}
            </div> */}
          </>
        )}
      </div>
    </Layout>
  );
}
